import React from "react";
import {
  FiFacebook,
  FiGlobe,
  FiImage,
  FiMail,
  FiMapPin,
  FiPhone,
  FiPlus,
  FiSave,
  FiUsers,
} from "react-icons/fi";

import { toAbsolutePublicUrl } from "../../../../services/mediaLibraryServices";

import AdminButton from "../../_shared/ui/AdminButton";
import AdminDrawer from "../../_shared/ui/AdminDrawer";
import AdminField from "../../_shared/ui/AdminField";
import AdminRichTextEditor from "../../_shared/ui/AdminRichTextEditor";

import {
  ASSOCIATION_CATEGORY_SUGGESTIONS,
  ASSOCIATIONS_FORM_HELPERS,
} from "../data/associationsAdminData";

export default function AssociationsFormDrawer({
  open,
  form,
  fieldErrors,
  saving,
  isEdit,
  onClose,
  onChange,
  onSave,
  onOpenMediaPicker,
}) {
  const logoUrl = form.logo_asso ? toAbsolutePublicUrl(form.logo_asso) : "";

  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      title={isEdit ? "Modifier l’association" : "Nouvelle association"}
      subtitle={
        isEdit
          ? "Mettez à jour la fiche puis enregistrez les modifications."
          : "Ajoutez une association à l’annuaire communal."
      }
      icon={FiUsers}
      size="full"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <AdminButton variant="secondary" onClick={onClose}>
            Annuler
          </AdminButton>

          <AdminButton
            icon={isEdit ? FiSave : FiPlus}
            variant="primary"
            loading={saving}
            onClick={onSave}
          >
            {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Créer"}
          </AdminButton>
        </div>
      }
    >
      <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
        <div className="space-y-6">
          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Identité de l’association
            </h3>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <AdminField
                label="Nom de l’association"
                required
                error={fieldErrors.nom_asso}
              >
                <input
                  value={form.nom_asso}
                  onChange={(event) =>
                    onChange("nom_asso", event.target.value)
                  }
                  placeholder="Nom de l’association"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField label="Catégorie">
                <input
                  value={form.categorie}
                  onChange={(event) =>
                    onChange("categorie", event.target.value)
                  }
                  placeholder="Sport, Culture, Solidarité..."
                  list="association-form-categories"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />

                <datalist id="association-form-categories">
                  {ASSOCIATION_CATEGORY_SUGGESTIONS.map((category) => (
                    <option key={category} value={category} />
                  ))}
                </datalist>
              </AdminField>
            </div>
          </section>

          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Logo et liens
            </h3>

            <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_1fr]">
              <AdminField
                label="Logo"
                help="Vous pouvez saisir une URL ou choisir une image dans la photothèque."
              >
                <div className="flex flex-col gap-3 sm:flex-row">
                  <input
                    value={form.logo_asso}
                    onChange={(event) =>
                      onChange("logo_asso", event.target.value)
                    }
                    placeholder="https://... ou /uploads/..."
                    className="min-h-12 flex-1 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                  />

                  <AdminButton
                    icon={FiImage}
                    variant="soft"
                    onClick={onOpenMediaPicker}
                    className="sm:min-w-[160px]"
                  >
                    Photothèque
                  </AdminButton>
                </div>
              </AdminField>

              <AdminField label="Site web">
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiGlobe className="text-slate-400" />

                  <input
                    value={form.website_asso}
                    onChange={(event) =>
                      onChange("website_asso", event.target.value)
                    }
                    placeholder="https://..."
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>

              <AdminField label="Facebook">
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiFacebook className="text-slate-400" />

                  <input
                    value={form.facebook_asso}
                    onChange={(event) =>
                      onChange("facebook_asso", event.target.value)
                    }
                    placeholder="https://facebook.com/..."
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>
            </div>
          </section>

          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Contact
            </h3>

            <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <AdminField label="Nom du contact">
                <input
                  value={form.contact_asso}
                  onChange={(event) =>
                    onChange("contact_asso", event.target.value)
                  }
                  placeholder="Nom du référent"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField label="Fonction">
                <input
                  value={form.fonction_asso}
                  onChange={(event) =>
                    onChange("fonction_asso", event.target.value)
                  }
                  placeholder="Président, secrétaire..."
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField label="Téléphone">
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiPhone className="text-slate-400" />

                  <input
                    value={form.tel_asso}
                    onChange={(event) =>
                      onChange("tel_asso", event.target.value)
                    }
                    placeholder="05..."
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>

              <AdminField label="Email">
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiMail className="text-slate-400" />

                  <input
                    type="email"
                    value={form.mail_asso}
                    onChange={(event) =>
                      onChange("mail_asso", event.target.value)
                    }
                    placeholder="contact@association.fr"
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>
            </div>
          </section>

          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Adresse et localisation
            </h3>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <AdminField label="Adresse">
                <textarea
                  value={form.adresse_asso}
                  onChange={(event) =>
                    onChange("adresse_asso", event.target.value)
                  }
                  rows={3}
                  placeholder="Adresse complète"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField
                label="Coordonnées GPS"
                help="Format conseillé : 44.8742,-0.6413"
              >
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiMapPin className="text-slate-400" />

                  <input
                    value={form.gps_asso}
                    onChange={(event) =>
                      onChange("gps_asso", event.target.value)
                    }
                    placeholder="44.8742,-0.6413"
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>
            </div>
          </section>

          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Description
            </h3>

            <div className="mt-4">
              <AdminRichTextEditor
                value={form.description_asso}
                onChange={(html) => onChange("description_asso", html)}
                placeholder="Présentez l’association, ses activités, ses horaires ou ses informations utiles..."
              />
            </div>
          </section>
        </div>

        <aside className="space-y-5">
          <div className="overflow-hidden rounded-[1.7rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
            <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
              <p className="text-sm font-bold text-slate-950 dark:text-white">
                Aperçu logo
              </p>
            </div>

            <div className="p-4">
              {logoUrl ? (
                <>
                  <div className="flex aspect-square items-center justify-center rounded-[1.25rem] bg-white p-5 dark:bg-slate-900">
                    <img
                      src={logoUrl}
                      alt="Logo"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>

                  <p className="mt-3 break-all text-xs leading-5 text-slate-500 dark:text-slate-400">
                    {logoUrl}
                  </p>
                </>
              ) : (
                <div className="flex aspect-square flex-col items-center justify-center rounded-[1.25rem] bg-white text-center text-slate-400 dark:bg-slate-900 dark:text-slate-500">
                  <FiImage className="text-4xl" />

                  <p className="mt-3 text-sm font-bold">Aucun logo</p>

                  <p className="mt-1 max-w-[220px] text-xs leading-5">
                    Ajoutez un logo pour identifier l’association.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
            <p className="text-sm font-bold text-slate-950 dark:text-white">
              Publication
            </p>

            <button
              type="button"
              onClick={() => onChange("is_active", !form.is_active)}
              className={`mt-3 flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition ${
                form.is_active
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                  : "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"
              }`}
            >
              <span>
                <span className="block text-sm font-bold">
                  {form.is_active
                    ? "Association publiée"
                    : "Association en brouillon"}
                </span>

                <span className="mt-0.5 block text-xs opacity-75">
                  {form.is_active
                    ? "Elle sera visible sur le site public."
                    : "Elle restera masquée du public."}
                </span>
              </span>

              <span
                className={`h-6 w-11 rounded-full p-1 transition ${
                  form.is_active ? "bg-emerald-500" : "bg-amber-500"
                }`}
              >
                <span
                  className={`block h-4 w-4 rounded-full bg-white transition ${
                    form.is_active ? "translate-x-5" : ""
                  }`}
                />
              </span>
            </button>
          </div>

          <div className="rounded-[1.7rem] border border-sky-100 bg-sky-50 p-4 dark:border-sky-500/20 dark:bg-sky-500/10">
            <p className="text-sm font-bold text-sky-900 dark:text-sky-200">
              Conseils pratiques
            </p>

            <ul className="mt-3 space-y-2">
              {ASSOCIATIONS_FORM_HELPERS.map((helper) => (
                <li
                  key={helper}
                  className="text-sm leading-6 text-sky-800 dark:text-sky-200"
                >
                  • {helper}
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </AdminDrawer>
  );
}